import type { IBatchResult, IOperationLogEntry, OperationStatus } from '../models/Operation';
import type { IAuditLog } from './AuditLog';
import { type IDriveItemRef, type IDriveWriter, NameConflictError } from './DriveWriter';
import { hasValue, type ILibraryMetadata } from '../tagging/Tagging';

export type BatchState = 'applied' | 'undone' | 'partiallyUndone';

/** One apply batch, as reconstructed from the audit log. */
export interface IBatchSummary {
  batchId: string;
  startedAt: string;
  user: string;
  operations: IOperationLogEntry[];
  succeeded: number;
  skipped: number;
  failed: number;
  /** Successful operations Undo can reverse. */
  reversible: IOperationLogEntry[];
  /** Folders the batch created to hold quarantined items; removed on undo once empty. */
  createdFolders: IOperationLogEntry[];
  /** Operation IDs already reversed by an undo. */
  undone: { [operationId: string]: boolean };
  state: BatchState;
  /** Only the newest batch that isn't fully undone can be undone (see summarizeBatches). */
  canUndo: boolean;
}

export interface IUndoCheck {
  /** Operations that would be reversed. */
  ready: number;
  /** Operations that can't be reversed as things stand, with why. */
  problems: { entry: IOperationLogEntry; message: string }[];
}

/** Read access used by the checks; lets check() overlay simulated state. */
interface IView {
  getItem(itemId: string): Promise<IDriveItemRef | undefined>;
  getChild(parentId: string, name: string): Promise<IDriveItemRef | undefined>;
}

export interface IUndoEngineOptions {
  writer: IDriveWriter;
  audit: IAuditLog;
  libraryId: string;
  libraryName: string;
  user: string;
  now?: () => Date;
  idSuffix?: () => string;
  /** Needed to undo tagging batches (column values). */
  metadata?: ILibraryMetadata;
  /** Needed to undo changes to the firm's records (rows in its lists). */
  records?: IRecordStore;
}

/** Reading and writing a row's values in one of the firm's lists, by the list's address name. */
export interface IRecordStore {
  getFields(list: string, itemId: string, columns: string[]): Promise<{ [column: string]: unknown } | undefined>;
  setFields(list: string, itemId: string, fields: { [column: string]: unknown }): Promise<void>;
}

const TIMESTAMP: RegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

/** Equal as stored. SharePoint keeps times to the second, so a time we wrote with milliseconds comes back without them. */
function sameValue(a: unknown, b: unknown): boolean {
  const x: string = hasValue(a) ? String(a) : '';
  const y: string = hasValue(b) ? String(b) : '';
  if (x !== y && TIMESTAMP.test(x) && TIMESTAMP.test(y)) {
    return Math.floor(Date.parse(x) / 1000) === Math.floor(Date.parse(y) / 1000);
  }
  return x === y;
}

function pad(n: number, width: number): string {
  let s: string = String(n);
  while (s.length < width) {
    s = `0${s}`;
  }
  return s;
}

function describe(error: unknown): string {
  if (error instanceof NameConflictError) {
    return error.message;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e: any = error;
  switch (e?.statusCode) {
    case 403: return 'You don\'t have permission to change this item.';
    case 423: return 'The item is locked — it may be open, checked out, or under a retention hold.';
    default: return String(e?.message ?? e ?? 'Unknown error');
  }
}

/**
 * Groups audit-log entries into batches, newest first, and works out which have been undone.
 * Batches must be undone newest-first: an older batch's items may since have been moved by
 * a newer one (e.g. into a folder that is now in quarantine), so only the newest batch that
 * isn't fully undone is offered for undo.
 */
export function summarizeBatches(entries: IOperationLogEntry[]): IBatchSummary[] {
  const undone: { [operationId: string]: boolean } = {};
  for (const e of entries) {
    if (e.action === 'undo' && e.undoOf && (e.status === 'success' || e.status === 'skipped')) {
      undone[e.undoOf] = true;
    }
  }

  const byBatch: { [batchId: string]: IOperationLogEntry[] } = {};
  const order: string[] = [];
  for (const e of entries) {
    if (e.action === 'undo') {
      continue;
    }
    if (!byBatch[e.batchId]) {
      byBatch[e.batchId] = [];
      order.push(e.batchId);
    }
    byBatch[e.batchId].push(e);
  }

  const summaries: IBatchSummary[] = order.map((batchId): IBatchSummary => {
    const operations: IOperationLogEntry[] = byBatch[batchId].slice().sort((a, b) => a.operationId.localeCompare(b.operationId));
    const work: IOperationLogEntry[] = operations.filter(e => e.action !== 'createFolder' && e.action !== 'createColumn');
    const reversible: IOperationLogEntry[] = operations.filter(e => e.status === 'success' && e.reversible && e.action !== 'createFolder' && e.action !== 'createColumn');
    const reversedCount: number = reversible.filter(e => undone[e.operationId]).length;
    const batchUndone: { [operationId: string]: boolean } = {};
    for (const e of operations) {
      if (undone[e.operationId]) {
        batchUndone[e.operationId] = true; // Includes folders the batch created and undo removed.
      }
    }
    return {
      batchId,
      startedAt: operations[0].timestamp,
      user: operations[0].user,
      operations,
      succeeded: work.filter(e => e.status === 'success').length,
      skipped: work.filter(e => e.status === 'skipped').length,
      failed: work.filter(e => e.status === 'failed').length,
      reversible,
      createdFolders: operations.filter(e => e.action === 'createFolder' && e.status === 'success' && !undone[e.operationId]),
      undone: batchUndone,
      state: reversible.length > 0 && reversedCount === reversible.length ? 'undone'
        : reversedCount > 0 ? 'partiallyUndone' : 'applied',
      canUndo: false
    };
  }).sort((a, b) => b.startedAt.localeCompare(a.startedAt));

  const newestOpen: IBatchSummary | undefined = summaries.filter(s => s.state !== 'undone' && s.reversible.length > 0)[0];
  if (newestOpen) {
    newestOpen.canUndo = true;
  }
  return summaries;
}

/**
 * Reverses a batch: every successful, reversible operation is moved back to its original
 * folder and name, newest operation first. The same safety rules as applying: an item that
 * changed since the batch is left alone, nothing is overwritten, and every step is logged.
 * Folders the app created to hold quarantined items are removed once empty (to the site's
 * recycle bin); if anything else has been put in one, it is left in place.
 */
export class UndoEngine {
  constructor(private readonly _o: IUndoEngineOptions) {}

  /** Checks every operation against live SharePoint without writing anything. */
  public async check(batch: IBatchSummary): Promise<IUndoCheck> {
    const { writer } = this._o;
    const result: IUndoCheck = { ready: 0, problems: [] };
    // Effects of earlier undo steps, so later checks see names they free or take.
    const where: { [itemId: string]: { parentId: string; name: string } } = {};
    const names: { [parentId: string]: { [lowerName: string]: string } } = {};
    const view: IView = {
      getItem: async id => {
        const live: IDriveItemRef | undefined = await writer.getItem(id);
        return live && where[id] ? { ...live, parentId: where[id].parentId, name: where[id].name } : live;
      },
      getChild: async (parentId, name) => {
        const overlay: string | undefined = names[parentId]?.[name.toLowerCase()];
        if (overlay !== undefined) {
          return overlay ? { id: overlay, name, parentId, isFolder: false } : undefined;
        }
        return writer.getChild(parentId, name);
      }
    };
    for (const entry of this._pending(batch)) {
      if (entry.action === 'tag' || entry.action === 'record') {
        const problem: string | undefined = entry.action === 'tag' ? await this._tagProblem(entry) : await this._recordProblem(entry);
        if (problem === undefined) {
          result.ready++;
        } else if (problem !== 'already') {
          result.problems.push({ entry, message: problem });
        }
        continue;
      }
      if (entry.action === 'createFolder') {
        // Removed once emptied by the earlier steps, freeing its name. (If it still holds
        // anything at the time, the real undo leaves it — and whatever needs its name fails safely.)
        (names[entry.destinationParentId] = names[entry.destinationParentId] || {})[entry.newName.toLowerCase()] = '';
        continue;
      }
      const problem: string | undefined = await this._problem(entry, await view.getItem(entry.itemId), view);
      if (problem === undefined) {
        result.ready++;
        (names[entry.destinationParentId] = names[entry.destinationParentId] || {})[entry.newName.toLowerCase()] = '';
        (names[entry.sourceParentId] = names[entry.sourceParentId] || {})[entry.originalName.toLowerCase()] = entry.itemId;
        where[entry.itemId] = { parentId: entry.sourceParentId, name: entry.originalName };
      } else if (problem !== 'already') {
        result.problems.push({ entry, message: problem });
      }
    }
    return result;
  }

  public async undo(batch: IBatchSummary, onProgress?: (done: number, total: number) => void): Promise<IBatchResult> {
    const { writer, audit } = this._o;
    const now: () => Date = this._o.now ?? (() => new Date());
    const suffix: string = (this._o.idSuffix ?? (() => Math.random().toString(36).substring(2, 6).toUpperCase()))();
    const started: string = now().toISOString();
    const day: string = started.substring(0, 10).replace(/-/g, '');
    const batchId: string = `UNDO-${day}-${started.substring(11, 19).replace(/:/g, '')}-${suffix}`;
    const result: IBatchResult = { batchId, entries: [] };

    try {
      await audit.ensure();
    } catch (error) {
      result.abortedReason = `Nothing was undone. ${describe(error)}`;
      return result;
    }

    // Work from the log as it is now, so steps already undone (e.g. by an earlier, interrupted
    // undo) are never repeated, even if the caller's summary is stale.
    const fresh: IBatchSummary = summarizeBatches(await audit.readEntries(this._o.libraryId))
      .filter(b => b.batchId === batch.batchId)[0] ?? batch;
    const pending: IOperationLogEntry[] = this._pending(fresh);
    let done: number = 0;
    try {
      for (const entry of pending) {
        let status: OperationStatus = 'success';
        let errorMessage: string | undefined;
        try {
          const live: IDriveItemRef | undefined = entry.action === 'record' ? undefined : await writer.getItem(entry.itemId);
          const problem: string | undefined = entry.action === 'createFolder' ? undefined
            : entry.action === 'tag' ? await this._tagProblem(entry)
            : entry.action === 'record' ? await this._recordProblem(entry) : await this._problem(entry, live, writer);
          if (entry.action === 'record') {
            if (problem === 'already') {
              status = 'skipped';
              errorMessage = 'Already back as it was.';
            } else if (problem) {
              status = 'failed';
              errorMessage = problem;
            } else {
              await this._o.records!.setFields(entry.sourceParentId, entry.itemId, JSON.parse(entry.sourcePath || '{}'));
            }
          } else if (entry.action === 'tag') {
            if (problem === 'already') {
              status = 'skipped';
              errorMessage = 'Tags already cleared.';
            } else if (problem) {
              status = 'failed';
              errorMessage = problem;
            } else {
              await this._o.metadata!.setFields(entry.itemId, JSON.parse(entry.sourcePath || '{}'));
            }
          } else if (entry.action === 'createFolder') {
            if (!live) {
              status = 'skipped';
              errorMessage = 'Already removed.';
            } else if ((live.childCount ?? 0) > 0) {
              status = 'skipped';
              errorMessage = 'Left in place because it still contains items.';
            } else {
              await writer.deleteItem(entry.itemId);
              errorMessage = 'Empty folder created by the app; removed to the recycle bin.';
            }
          } else if (problem === 'already') {
            status = 'skipped';
            errorMessage = 'Already back in its original place.';
          } else if (problem) {
            status = 'failed';
            errorMessage = problem;
          } else {
            await writer.moveItem(
              entry.itemId,
              live!.parentId !== entry.sourceParentId ? entry.sourceParentId : undefined,
              live!.name !== entry.originalName ? entry.originalName : undefined
            );
          }
        } catch (error) {
          status = 'failed';
          errorMessage = describe(error);
        }

        const logEntry: IOperationLogEntry = {
          operationId: `OP-${day}-${suffix}-${pad(result.entries.length + 1, 4)}`,
          batchId,
          changeId: entry.changeId,
          action: 'undo',
          itemKind: entry.itemKind,
          itemId: entry.itemId,
          libraryId: this._o.libraryId,
          libraryName: this._o.libraryName,
          sourcePath: entry.destinationPath,
          sourceParentId: entry.destinationParentId,
          originalName: entry.newName,
          destinationPath: entry.sourcePath,
          destinationParentId: entry.sourceParentId,
          newName: entry.originalName,
          reason: `Undo of ${entry.operationId} (${entry.batchId})`,
          status,
          errorMessage,
          reversible: false,
          user: this._o.user,
          timestamp: now().toISOString(),
          undoOf: entry.operationId
        };
        result.entries.push(logEntry);
        await audit.write(logEntry);
        done++;
        if (onProgress) {
          onProgress(done, pending.length);
        }
      }
    } catch (error) {
      result.abortedReason = `Stopped after ${done} of ${pending.length} steps because the audit log could not be written: ` +
        `${describe(error)} Steps already undone are listed below.`;
    }
    return result;
  }

  /** Operations not yet undone, newest first — including folders the batch created. */
  private _pending(batch: IBatchSummary): IOperationLogEntry[] {
    return batch.reversible
      .filter(e => !batch.undone[e.operationId])
      .concat(batch.createdFolders)
      .sort((a, b) => b.operationId.localeCompare(a.operationId));
  }

  /** For a record entry: 'already' if back as it was, a message if someone changed it since, undefined if it can be undone. */
  private async _recordProblem(entry: IOperationLogEntry): Promise<string | undefined> {
    if (!this._o.records) {
      return 'Changes to records can only be undone from the web app.';
    }
    const written: { [column: string]: unknown } = JSON.parse(entry.destinationPath || '{}');
    const previous: { [column: string]: unknown } = JSON.parse(entry.sourcePath || '{}');
    const columns: string[] = Object.keys(written);
    const current: { [column: string]: unknown } | undefined = await this._o.records.getFields(entry.sourceParentId, entry.itemId, columns);
    if (!current) {
      return `"${entry.originalName}" isn't in the list any more, so it was left as it is.`;
    }
    if (columns.every(c => sameValue(current[c], previous[c]))) {
      return 'already';
    }
    if (!columns.every(c => sameValue(current[c], written[c]))) {
      return `"${entry.originalName}" was changed since, so it was left as it is.`;
    }
    return undefined;
  }

  /** For a tagging entry: 'already' if cleared, a message if the values changed since, undefined if it can be undone. */
  private async _tagProblem(entry: IOperationLogEntry): Promise<string | undefined> {
    if (!this._o.metadata) {
      return 'Tags can only be undone from the web app.';
    }
    const written: { [column: string]: unknown } = JSON.parse(entry.destinationPath || '{}');
    const previous: { [column: string]: unknown } = JSON.parse(entry.sourcePath || '{}');
    const current: { [column: string]: unknown } = await this._o.metadata.getFields(entry.itemId);
    const columns: string[] = Object.keys(written);
    if (columns.every(c => sameValue(current[c], previous[c]))) {
      return 'already';
    }
    if (!columns.every(c => sameValue(current[c], written[c]))) {
      return `The tags on "${entry.originalName}" were changed since, so they were left as they are.`;
    }
    return undefined;
  }

  /** Why an operation can't be reversed now; 'already' if it has been; undefined if it can. */
  private async _problem(entry: IOperationLogEntry, live: IDriveItemRef | undefined, view: IView): Promise<string | undefined> {
    const writer: IView = view;
    if (!live) {
      return `"${entry.newName}" no longer exists.`;
    }
    if (live.parentId === entry.sourceParentId && live.name === entry.originalName) {
      return 'already';
    }
    if (live.parentId !== entry.destinationParentId || live.name !== entry.newName) {
      return `"${entry.destinationPath}" has been moved or renamed since, so it was left where it is.`;
    }
    if (!(await writer.getItem(entry.sourceParentId))) {
      return `The original folder of "${entry.sourcePath}" no longer exists.`;
    }
    const taken: IDriveItemRef | undefined = await writer.getChild(entry.sourceParentId, entry.originalName);
    if (taken && taken.id !== entry.itemId) {
      return `Something named "${entry.originalName}" is already back at "${entry.sourcePath}" — undoing would overwrite it.`;
    }
    return undefined;
  }
}
